/**
 * Redirect-based social sign-in for the installed web app (PWA).
 *
 * In a browser tab the three providers sign the player in through a popup that hands
 * the token back over `window.opener`. An installed iOS web app opens popups in a
 * separate in-app browser with no opener and its own storage, so nothing ever comes
 * back. The standard answer is to let the *page itself* go to the provider and return:
 *
 *   app  -->  provider's authorize page  -->  `/auth/social/return#<token>...`  -->  app
 *
 * Each provider can put the token straight into the URL fragment, so no backend is
 * involved and the backend keeps receiving exactly what the popups gave it:
 *   - Google: OpenID Connect `response_type=id_token` (an ID token for the same web
 *     client id, so the backend's audience check is unchanged);
 *   - Facebook: `response_type=token` (a user access token, as `FB.login` returns);
 *   - Apple: `response_type=code id_token` with `response_mode=fragment`. Apple only
 *     allows that with no scope - asking for name or email forces a form POST, which
 *     needs a server - so the ID token may lack the email; the backend already copes
 *     (it synthesises an address from the Apple id). Apple returns to the registered
 *     return URL, whose static page forwards here when it finds no opener.
 *
 * What was started is remembered in localStorage with a random `state`; the return is
 * only accepted when the state matches. The return URL is reached as a fresh page load,
 * so the boot (`useSocialRedirectReturn`) consumes it and dispatches the same login
 * action the popup path would.
 *
 * Used only when `shouldUseSocialRedirect()` says so; browser tabs keep their popups.
 */
import { env } from '../../utils/env';
import LS from '../../utils/localStorageUtils';
import { isStandaloneDisplay } from '../pwa/installPrompt';
import { WEB_CLIENT_ID } from './google.config.web';

export type SocialRedirectProvider = 'google' | 'apple' | 'facebook';

/** Where Google and Facebook return to; the app boots here and consumes the fragment. */
export const SOCIAL_RETURN_PATH = '/auth/social/return';

const STORAGE_KEY = 'social_redirect_login';
/** A round trip older than this is not trusted (the player wandered off mid-login). */
const MAX_AGE_MS = 10 * 60 * 1000;

/** Keep in step with the scope `facebookSignIn.web.ts` asks `FB.login` for. */
const FACEBOOK_SCOPE = 'public_profile,email,user_birthday,user_gender,user_location';

type PendingRedirect = { provider: SocialRedirectProvider; state: string; startedAt: number };

export type SocialRedirectResult =
	| { provider: SocialRedirectProvider; token: string }
	| { provider: SocialRedirectProvider; error: string };

/** Redirect flows are for the installed app only; a browser tab keeps the popups. */
export const shouldUseSocialRedirect = (): boolean =>
	typeof window !== 'undefined' && isStandaloneDisplay();

export const socialReturnUrl = (): string => `${window.location.origin}${SOCIAL_RETURN_PATH}`;

const randomToken = (): string => {
	const bytes = new Uint8Array(16);
	if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
		crypto.getRandomValues(bytes);
	} else {
		for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
	}
	return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
};

const requiredEnv = (key: string): string => {
	const value = env(key)?.trim();
	if (!value) throw new Error(`Missing ${key} for redirect social login`);
	return value;
};

function buildAuthorizeUrl(provider: SocialRedirectProvider, state: string, nonce: string): string {
	switch (provider) {
		case 'google': {
			const params = new URLSearchParams({
				client_id: WEB_CLIENT_ID,
				redirect_uri: socialReturnUrl(),
				response_type: 'id_token',
				scope: 'openid email profile',
				nonce,
				state,
				prompt: 'select_account',
			});
			return `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
		}
		case 'facebook': {
			const params = new URLSearchParams({
				client_id: requiredEnv('FACEBOOK_APP_ID'),
				redirect_uri: socialReturnUrl(),
				response_type: 'token',
				scope: FACEBOOK_SCOPE,
				state,
			});
			return `https://www.facebook.com/v19.0/dialog/oauth?${params}`;
		}
		case 'apple': {
			const params = new URLSearchParams({
				client_id: requiredEnv('APPLE_WEB_CLIENT_ID'),
				redirect_uri: requiredEnv('APPLE_WEB_REDIRECT_URI'),
				response_type: 'code id_token',
				response_mode: 'fragment',
				nonce,
				state,
			});
			return `https://appleid.apple.com/auth/authorize?${params}`;
		}
	}
}

/**
 * Sends the page to the provider. The returned promise never settles: the document is
 * on its way out, and the login resumes from the return URL on the next boot.
 */
export function beginSocialRedirect(provider: SocialRedirectProvider): Promise<never> {
	const state = randomToken();
	const nonce = randomToken();
	const url = buildAuthorizeUrl(provider, state, nonce);

	const pending: PendingRedirect = { provider, state, startedAt: Date.now() };
	LS.set(STORAGE_KEY, JSON.stringify(pending));

	window.location.assign(url);
	return new Promise<never>(() => {});
}

const readPending = (): PendingRedirect | null => {
	const raw = LS.get(STORAGE_KEY);
	if (!raw) return null;
	try {
		const parsed = JSON.parse(raw) as Partial<PendingRedirect>;
		if (!parsed.provider || !parsed.state || typeof parsed.startedAt !== 'number') return null;
		return parsed as PendingRedirect;
	} catch {
		return null;
	}
};

/** Providers answer in the fragment; errors sometimes come in the query. Read both. */
const readReturnParams = (): URLSearchParams => {
	const params = new URLSearchParams(window.location.search);
	const hash = window.location.hash.replace(/^#/, '');
	new URLSearchParams(hash).forEach((value, key) => params.set(key, value));
	return params;
};

/**
 * Picks up a provider's answer when the app boots on the return URL, and cleans the URL
 * either way so a reload does not replay it. Null when this is not a return.
 */
export function consumeSocialRedirectReturn(): SocialRedirectResult | null {
	if (typeof window === 'undefined') return null;
	if (window.location.pathname.replace(/\/$/, '') !== SOCIAL_RETURN_PATH) return null;

	const params = readReturnParams();
	const pending = readPending();
	LS.remove(STORAGE_KEY);
	window.history.replaceState(null, '', '/');

	if (!pending) {
		console.warn('[socialRedirect] return without a pending login; ignored');
		return null;
	}

	const { provider } = pending;
	if (Date.now() - pending.startedAt > MAX_AGE_MS) {
		return { provider, error: 'The sign-in took too long. Please try again.' };
	}
	if (params.get('state') !== pending.state) {
		return { provider, error: 'The sign-in could not be verified. Please try again.' };
	}

	const error = params.get('error');
	if (error) {
		return { provider, error: params.get('error_description') || error };
	}

	const token = provider === 'facebook' ? params.get('access_token') : params.get('id_token');
	if (!token) {
		return { provider, error: 'No sign-in token was returned. Please try again.' };
	}

	return { provider, token };
}
